import React, { useState, useCallback, useEffect } from 'react';

import dashboardApi from 'services/apis/dashboard';
import { Table, Text, Link, Button } from 'v3/components';
import { formatDateTime } from 'v3/utils/formatter';
import moment from 'moment';
import { useQuery } from 'hooks';
import Card, { Number, Description, SeeAll } from './Card';

export default function PreLoads({ dashboard, name, id }) {
  const [isFetching, setFetching] = useState(false);
  const [data, setData] = useState({});
  const [filter, setFilter] = useState({});
  const [currentPage, setCurrentPage] = useState({});
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(false);
  const [daysFromNow, setDaysFromNow] = useState(3);
  const queryParams = useQuery();

  useEffect(() => {
    const startDate = moment().subtract(filter.start_date, 'days');
    setDaysFromNow(moment().diff(startDate, 'days'));
  }, [filter.start_date]);

  function setQueryParams(filters) {
    const { start_date, end_date } = filters;

    if (start_date) {
      queryParams.set('start_date', start_date);
    }

    if (end_date) {
      queryParams.set('end_date', end_date);
    }

  }

  const fetchPreLoads = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      const {
        clients = [],
        negotiators = [],
        costCenters = [],
        pcps = [],
        companies = [],
        vehicleTypes = [],
        vehicleBodies = [],
        implementTypes = [],
        page = 1,
        start_date,
        end_date,
        dueDatePreLoads,
        clientOrderNumber,
        preLoadStatus
      } = filters;

      if (page === 1) {
        setFetching(true);
      }

      setQueryParams(filters);
      setFilter(filters);
      setCurrentPage(+page);

      const response = await dashboardApi.get('pre-loads', {
        params: {
          clients: clients?.map(client => client.id),
          start_date,
          negotiators: negotiators?.map(negotiator => negotiator?.id),
          costCenters: costCenters?.map(costCenter => costCenter?.id),
          pcps: pcps?.map(pcp => pcp?.id),
          companies: companies?.map(company => company?.id),
          end_date: end_date || undefined,
          vehicleTypes: vehicleTypes?.map(type => type.id),
          vehicleBodies: vehicleBodies?.map(body => body.id),
          implementTypes: implementTypes?.map(implement => implement.id),
          dueDatePreLoads: dueDatePreLoads,
          clientOrderNumber: clientOrderNumber,
          preLoadStatus: preLoadStatus?.value,
          page,
        },
      });

      setPagination(response.data?.last?.pagination);

      if (page === 1) {
        setData(response.data);
      } else {
        setData((oldData) => ({
          ...oldData,
          last: {
            ...oldData.last,
            data: [...oldData.last.data, ...response.data.last.data],
          },
        }));
      }
    } catch (ex) {
      // Handle exception
    } finally {
      setFetching(false);
      setLoading(false);
    }
  }, []);

  return (
    <Card
      title="Pré cargas"
      isLoading={isFetching}
      fetchFn={fetchPreLoads}
      onFiltersChanged={fetchPreLoads}
      dashboard={dashboard}
      name={name}
      excludeFilterByVehicleTypes={false}
      excludeFilterByVehicleBodies={false}
      excludeFilterByImplementTypes={false}
      excludeFilterDueDatePreLoads={false}
      excludeFilterByClientOrderNumber={false}
      excludeFilterByPreLoadStatus={false}
      excludeFilterByTags={true}
      id={id}
      ListComponent={
        <>
          <Table striped>
            <thead>
              <tr>
                <th>
                  <Text weight="500" color="dark">
                    Nº Pré carga
                  </Text>
                </th>
                <th>
                  <Text weight="500" color="dark">
                    Empresa
                  </Text>
                </th>
                <th>
                  <Text weight="500" color="dark">
                    Status
                  </Text>
                </th>
                <th>
                  <Text weight="500" color="dark">
                    Centro de custo
                  </Text>
                </th>
                <th>
                  <Text weight="500" color="dark">
                    Razão Social - CPF/CNPJ
                  </Text>
                </th>
                <th>
                  <Text weight="500" color="dark">
                    Origem
                  </Text>
                </th>
                <th>
                  <Text weight="500" color="dark">
                    Destino
                  </Text>
                </th>
                <th>
                  <Text weight="500" color="dark">
                    Data de Vencimento da carga
                  </Text>
                </th>
                <th>
                  <Text weight="500" color="dark">
                    N Pedido Cliente
                  </Text>
                </th>
                <th>
                  <Text weight="500" color="dark">
                    Veículo
                  </Text>
                </th>
                <th>
                  <Text weight="500" color="dark">
                    Carroceria
                  </Text>
                </th>
                <th>
                  <Text weight="500" color="dark">
                    Implemento
                  </Text>
                </th>
              </tr>
            </thead>
            <tbody>
              {data?.last?.data?.map((preLoad) => (
                <tr key={preLoad.loadId}>
                  <th>
                    {
                      preLoad.status === 'Atendida' ? (
                        <Link to={`/cargas/${preLoad.loadId}`} target="_blank">
                          {preLoad.loadId}
                        </Link>
                      ) : (
                        <Link to={`/pre-cargas/${preLoad.loadId}`} target="_blank">
                          {preLoad.loadId}
                        </Link>
                      )
                    }
                  </th>
                  <th>
                    <Text type="regular" color="gray">
                      {preLoad.company}
                    </Text>
                  </th>
                  <th>
                    <Text type="regular" color="gray">
                      {preLoad.status}
                    </Text>
                  </th>
                  <th>
                    <Text type="regular" color="gray">
                      {preLoad.costCenter}
                    </Text>
                  </th>

                  <th>
                    <Text type="regular" color="gray">
                      {`${preLoad.clientSocialName} - ${preLoad.clientCgccpf}`}
                    </Text>
                  </th>
                  <th>
                    <Text type="regular" color="gray">
                      {preLoad.origin}
                    </Text>
                  </th>
                  <th>
                    <Text type="regular" color="gray">
                      {preLoad.destination}
                    </Text>
                  </th>
                  <th>
                    <Text type="regular" color="gray">
                      {formatDateTime(preLoad.dueDate)}
                    </Text>
                  </th>
                  <th>
                    <Text type="regular" color="gray">
                      {preLoad.orderNumber || 'Não cadastrado'}
                    </Text>
                  </th>
                  <th>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      {preLoad.vehiclesTypes?.length &&
                        preLoad.vehiclesTypes.split(',').map((vehicle, i) => (
                          <Text key={i} type="regular" color="gray">
                            {vehicle}
                          </Text>
                        ))}
                    </div>
                  </th>
                  <th>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      {preLoad.bodyTypes?.length &&
                        preLoad.bodyTypes.split(',').map((bodyType, i) => (
                          <Text key={i} type="regular" color="gray">
                            {bodyType}
                          </Text>
                        ))}
                    </div>
                  </th>
                  <th>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      {preLoad.implementTypes?.length &&
                        preLoad.implementTypes.split(',').map((implement, i) => (
                          <Text key={i} type="regular" color="gray">
                            {implement}
                          </Text>
                        ))}
                    </div>
                  </th>
                </tr>
              ))}
            </tbody>
          </Table>
          {pagination?.lastPage > 1 && pagination?.to < pagination?.total && (
            <div className="text-center mt-3 col-12">
              <Button
                className="mt-2 ml-auto align-item-center"
                onClick={() => {
                  fetchPreLoads({
                    page: currentPage + 1,
                    clients: filter?.clients,
                    start_date: filter?.start_date,
                    end_date: filter?.end_date,
                    costCenters: filter?.costCenters,
                    pcps: filter?.pcps,
                    negotiators: filter?.negotiators,
                    companies: filter?.companies,
                    vehicleTypes: filter?.vehicleTypes,
                    vehicleBodies: filter?.vehicleBodies,
                    implementTypes: filter?.implementTypes,
                    dueDatePreLoads: filter?.dueDatePreLoads,
                    clientOrderNumber: filter?.clientOrderNumber,
                    preLoadStatus: filter?.preLoadStatus,
                    filterByPeriod: filter?.filterByPeriod
                  });
                }}
                loading={loading}
              >
                <Text type="regular" color="white" truncate>
                  Ver mais
                </Text>
              </Button>
            </div>
          )}
          {!!data?.total && (
            <SeeAll
              to={{
                pathname: '/pre-cargas',
                search: queryParams.toString(),
              }}
            >
              ver todas as {data?.total} pré cargas
            </SeeAll>
          )}
        </>
      }
      NumericComponent={
        <div className="d-flex justify-content-center text-center flex-column">
          <Number color="warning">{data?.total}</Number>
          <Description>
            Pré cargas
            {daysFromNow >= 1 && daysFromNow < 61
              ? ` nos últimos ${daysFromNow} dias`
              : ` no período de  ${moment.utc(filter.start_date).format('DD/MM/YYYY')}
              até ${moment.utc(filter.end_date).format('DD/MM/YYYY')}`}
          </Description>
        </div>
      }
    />
  );
}
